/**
 * Guía de la primera partida.
 *
 * Un juego de cartas con reglas propias no se entiende mirándolo: hay tres
 * zonas, diez turnos, energía que sube y una condición de victoria que no es
 * "hacer más puntos". Sin explicarlo, la primera partida se juega a ciegas y
 * muchas personas no llegan a la segunda.
 *
 * Tres decisiones deliberadas:
 *
 * - **Cuatro pasos y fuera.** Un tutorial largo se salta. Estos son los cuatro
 *   datos sin los cuales no se puede tomar ni una decisión.
 * - **No bloquea el juego.** Se puede cerrar en cualquier momento y el tablero
 *   sigue detrás, jugable.
 * - **Se recuerda que ya se vio**, pero se puede volver a abrir. Nadie debería
 *   quedarse sin poder releer las reglas por haber cerrado una ventana.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Hand, Lightbulb, Swords, Trophy, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { RULES } from '@/types/game';

const STEPS = [
  {
    icon: Swords,
    title: 'Tres zonas, una batalla',
    body: `Cada partida se juega en tres ubicaciones a la vez. Cada una tiene su propio efecto, y cambian en cada partida.`,
  },
  {
    icon: Hand,
    title: 'Coloca cartas con energía',
    body: `Tienes tantos puntos de energía como el turno en que estás, hasta ${RULES.maxEnergyPerTurn}. Lo que no gastes se pierde: no se acumula.`,
  },
  {
    icon: Trophy,
    title: 'Gana quien controle más zonas',
    body: `Al acabar el turno ${RULES.maxTurns} gana quien tenga más poder en más zonas. Arrasar en una sola no sirve de nada si pierdes las otras dos.`,
  },
  {
    icon: Lightbulb,
    title: 'Dos gestos que conviene conocer',
    body: 'Mantén pulsada cualquier carta para verla grande y leer lo que hace. Y si no sabes qué jugar, el botón de la bombilla te recomienda una jugada y te explica por qué.',
  },
] as const;

interface FirstMatchGuideProps {
  open: boolean;
  onClose: () => void;
}

export function FirstMatchGuide({ open, onClose }: FirstMatchGuideProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const close = () => {
    setStep(0);
    onClose();
  };

  if (!current) return null;
  const Icon = current.icon;

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-40 grid place-items-end bg-slate-950/70 p-3 backdrop-blur-xs sm:place-items-center"
          role="dialog"
          aria-label="Cómo se juega"
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="w-full max-w-sm rounded-2xl border border-white/15 bg-slate-900 p-5 shadow-2xl"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-400/15 text-amber-300">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <button
                type="button"
                onClick={close}
                aria-label="Cerrar la guía"
                className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-white/10 hover:text-slate-200"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <h2 className="text-lg leading-tight font-bold text-white">{current.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{current.body}</p>

            <div className="mt-5 flex items-center justify-between">
              {/* Los puntos dicen cuánto queda: sin ellos, "Siguiente" parece
                  que puede seguir para siempre. */}
              <div className="flex gap-1.5" aria-hidden>
                {STEPS.map((_, index) => (
                  <span
                    key={index}
                    className={`h-1.5 rounded-full transition-all ${
                      index === step ? 'w-5 bg-amber-300' : 'w-1.5 bg-slate-600'
                    }`}
                  />
                ))}
              </div>

              <Button size="sm" onClick={() => (isLast ? close() : setStep(step + 1))}>
                {isLast ? 'Empezar' : 'Siguiente'}
                {isLast ? null : <ChevronRight className="h-3 w-3" aria-hidden />}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
